import { ContentfulConstraint, ContentfulQuery, mergeConstraints } from 'services/ContentfulService/utils'
import useSWR from 'swr'
import { Article, parseArticle, summarizeArticle } from 'services/ContentfulService/models/articles'
import { IArticleFields } from 'generated/types/contentful'
import { ObjectNotFoundError } from 'services/ContentfulService/errors'
import { IDsConstraint } from 'services/ContentfulService/constraints'
import Logger from 'services/Logger'
import memoize from 'p-memoize'
import { createDataWrapper } from 'components/patterns/suspense/internals/DataWrapper'

export const articleFetcher = async (constraints: string, count: number, offset: number): Promise<Array<Article>> => {
	try {
		const parsed = JSON.parse(constraints)
		const constraintsObj = constraints ? mergeConstraints(parsed) : {}
		const query: ContentfulQuery = {
			order: '-fields.dateWritten',
			limit: count,
			content_type: 'article',
			skip: offset,
			...constraintsObj
		}
		const { fetchEntries } = await import('services/ContentfulService/client')
		const articleCollection = await fetchEntries<IArticleFields>(query)
		return articleCollection.items.map(item => parseArticle(item))
	} catch (err) {
		Logger.error(err)
		return []
	}
}

type ContentfulHookResponse<T> = [T | undefined, boolean, Error | undefined]

export interface UseArticlesInterface {
	constraints: ContentfulConstraint[]
	count?: number
	offset?: number
	id?: string
}

export const fetchArticles = async ({
	constraints = [],
	count = 100,
	offset = 0
}: UseArticlesInterface): // initialData?: DehydratedArticle[]
Promise<Array<Article>> => {
	return articleFetcher(JSON.stringify(constraints), count, offset)
}

export const useArticles = (
	{ constraints, count = 10, offset = 0 }: UseArticlesInterface,
	initialData?: Article[]
): // initialData?: DehydratedArticle[]
ContentfulHookResponse<Array<Article>> => {
	// const initialArticles = initialData?.map(article => hydrateArticle(article))
	const { data, error } = useSWR([JSON.stringify(constraints), count, offset], articleFetcher, { initialData })
	return [data, !data && !error, error]
}

export const getArticleBySlug = async (slug: string): Promise<Article> => {
	const constraints = [{ 'fields.slug': slug }]
	const articles = await articleFetcher(JSON.stringify(constraints), 1, 0)
	const article = articles.shift()
	if (article) return article
	throw new ObjectNotFoundError('article')
}

const getArticleById = memoize(async (id: string): Promise<Article> => {
	const constraints = [IDsConstraint([id])]
	const articles = await articleFetcher(JSON.stringify(constraints), 1, 0)
	const article = articles.shift()
	if (article) return article
	throw new ObjectNotFoundError('article')
})

export const buildArticleSummaryDataWrapper = (id: string) =>
	createDataWrapper(getArticleById, id).fork(article => summarizeArticle(article))
